// src/features/dashboard/DashboardPage.tsx
import type { ReactNode } from 'react';
import { HeroDashboardHeader } from './components/HeroDashboardHeader';
import { KPICard } from './components/KPICard';
import { StatCardSkeleton } from './components/ui/Skeleton';
import { DashboardSpotlight } from './components/DashboardSpotlight';
import { StatsGrid } from './components/StatsGrid';
import { RevenueOverview } from './components/RevenueOverview';
import { CustomersChart } from './components/CustomersChart';
import { ServiceSummary } from './components/ServiceSummary';
import { RecentOrdersTable } from './components/RecentOrdersTable';
import { RecentActivity } from './components/RecentActivity';
import { QuickActions } from './components/QuickActions';
import { useDashboard } from './hooks/useDashboard';
import type { QuickAction } from './types/dashboard.types';
import './styles/dashboard.css';

interface DashboardPageProps {
  /** Wire this to the app router once navigation is available. */
  onQuickAction?: (action: QuickAction) => void;
}

/**
 * Shared section shell for Dashboard V3 Phase 2's composition-level
 * hierarchy (Hero Command Center / Business Snapshot / Business
 * Operations / Insights / Activity). Presentation only -- it renders a
 * semantic `<section>`, an optional small eyebrow label for scannability,
 * and passes children through untouched. No data, props, or contracts
 * flow through this wrapper.
 */
function DashboardSection({
  label,
  ariaLabel,
  children,
}: {
  label?: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={ariaLabel} className="flex flex-col gap-5">
      {label && (
        <h2 className="px-1 text-xs font-semibold uppercase tracking-wider text-[var(--vania-text-tertiary,#6b7280)]">
          {label}
        </h2>
      )}
      {children}
    </section>
  );
}

/**
 * Representative Dashboard — backed by `GET /api/dashboard/summary`
 * via `useDashboard` (see `hooks/useDashboard.ts`,
 * `api/dashboard.api.ts`, `mappers/dashboard.mapper.ts`).
 *
 * Dashboard V3 composition: five stacked sections give the page a clear
 * reading order instead of one long undifferentiated column -- pure
 * re-grouping of existing widgets behind semantic `<section>`s and a
 * shared spacing rhythm (`--vania-section-gap` between sections,
 * `gap-5`/`--vania-panel-gap` within, both defined once in
 * `.vania-dashboard-stack`/`.vania-panel-row` in dashboard.css). No
 * widget's props, data source, or internal logic changed.
 *
 *   1. Hero Command Center -- HeroDashboardHeader, the 3-card KPI row
 *      (today's sales, active customers, wallet balance, sourced from
 *      the same `data` object as the rest of the page), and
 *      DashboardSpotlight. Kept together as-is: all three are the page's
 *      top-of-funnel attention band, so they read as one command center
 *      rather than three separate sections.
 *   2. Business Snapshot -- StatsGrid (Orders / Tickets; Services has
 *      its own widget below, Sales/Customers/Wallet live in the hero row,
 *      so nothing is duplicated).
 *   3. Business Operations -- ServiceSummary + QuickActions side by side
 *      on wide viewports (now an even 6/6 split now that ServiceSummary
 *      is a full panel in its own right, not a 5/7 split sized for a
 *      lightweight stat), stacked below `lg`.
 *   4. Activity -- RecentOrdersTable + RecentActivity. Phase 6: moved
 *      above Insights -- day-to-day operational content (recent orders,
 *      open tickets) is what a representative acts on, so it now reads
 *      before the trend charts rather than after them.
 *   5. Insights -- RevenueOverview + CustomersChart.
 *
 * Layout: 12-column responsive grid inside sections 3 and 4 (each row
 * marked `vania-panel-row` so both sides stretch to equal height); single
 * column stack below `lg`/`xl`. StatsGrid keeps its own auto-fit column
 * behavior.
 */
export function DashboardPage({ onQuickAction }: DashboardPageProps) {
  const { data, isInitialLoading, error, retry } = useDashboard();

  // Only shown when there's nothing cached to fall back to -- a
  // background refresh failure behind an already-visible dashboard
  // stays silent (same rule `DashboardSpotlight` follows).
  if (error && !data) {
    return (
      <div className="vania-dashboard vania-dashboard-stack">
        <div
          className="vania-glass-card vania-glass-card--elevated vania-spotlight-card vania-spotlight-card--error !flex-row items-center justify-between gap-4"
          data-animate="fade-in"
        >
          <span className="text-sm text-[var(--vania-accent-rose,#ff6b81)]">
            بارگذاری داشبورد با خطا مواجه شد.
          </span>
          <button
            type="button"
            onClick={retry}
            className="vania-spotlight-retry rounded-lg border border-[var(--vania-border-glass,rgba(255,255,255,0.12))] px-3 py-1.5 text-sm font-medium text-[var(--vania-text-primary,#f5f6fa)] hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.05))]"
          >
            تلاش دوباره
          </button>
        </div>
      </div>
    );
  }

  // KPI row data -- sourced entirely from the existing `data` contract.
  // No new fields, no mock/derived values: `todaysSales` is the last
  // (i.e. today's) point already present in `dailySales`, and the other
  // two reuse the same `stats` entries StatsGrid renders elsewhere.
  const todaysSales = data?.dailySales?.length ? data.dailySales[data.dailySales.length - 1] : null;
  const activeCustomersStat = data?.stats?.find((stat) => stat.id === 'active-customers') ?? null;
  const walletBalanceStat = data?.stats?.find((stat) => stat.id === 'wallet-balance') ?? null;

  return (
    <div className="vania-dashboard vania-dashboard-stack">
      {/* 1. Hero Command Center */}
      <section aria-label="Hero Command Center" className="flex flex-col gap-6">
        <HeroDashboardHeader representative={data?.representative ?? null} isInitialLoading={isInitialLoading} />

        <div className="vania-kpi-grid gap-4">
          {isInitialLoading ? (
            Array.from({ length: 3 }).map((_, i) => <StatCardSkeleton key={i} />)
          ) : (
            <>
              {todaysSales && (
                <KPICard
                  icon="sales"
                  label="فروش امروز"
                  value={todaysSales.amount}
                  format="currency"
                  subtitle="مجموع فروش امروز"
                  accent="sky"
                  index={0}
                />
              )}
              {activeCustomersStat && (
                <KPICard
                  icon="customers"
                  label={activeCustomersStat.label}
                  value={activeCustomersStat.value}
                  format={activeCustomersStat.format}
                  subtitle={activeCustomersStat.subtitle}
                  trend={activeCustomersStat.trend}
                  accent={activeCustomersStat.accent}
                  index={1}
                />
              )}
              {walletBalanceStat && (
                <KPICard
                  icon="wallet"
                  label={walletBalanceStat.label}
                  value={walletBalanceStat.value}
                  format={walletBalanceStat.format}
                  subtitle={walletBalanceStat.subtitle}
                  trend={walletBalanceStat.trend}
                  accent={walletBalanceStat.accent}
                  index={2}
                />
              )}
            </>
          )}
        </div>

        <DashboardSpotlight />
      </section>

      {/* 2. Business Snapshot */}
      <DashboardSection ariaLabel="Business Snapshot" label="نمای کلی کسب‌وکار">
        <StatsGrid stats={data?.stats ?? []} isInitialLoading={isInitialLoading} />
      </DashboardSection>

      {/* 3. Business Operations */}
      <DashboardSection ariaLabel="Business Operations" label="عملیات کسب‌وکار">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 vania-panel-row">
          <div className="lg:col-span-6">
            <ServiceSummary stats={data?.stats ?? []} isInitialLoading={isInitialLoading} />
          </div>
          <div className="lg:col-span-6">
            <QuickActions
              actions={data?.quickActions ?? []}
              {...(onQuickAction ? { onNavigate: onQuickAction } : {})}
            />
          </div>
        </div>
      </DashboardSection>

      {/* 4. Activity */}
      <DashboardSection ariaLabel="Activity" label="فعالیت‌های اخیر">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 vania-panel-row">
          <div className="xl:col-span-8">
            <RecentOrdersTable orders={data?.recentOrders ?? []} isInitialLoading={isInitialLoading} />
          </div>
          <div className="xl:col-span-4">
            <RecentActivity
              tickets={data?.recentTickets ?? []}
              events={data?.timeline ?? []}
              isInitialLoading={isInitialLoading}
            />
          </div>
        </div>
      </DashboardSection>

      {/* 5. Insights */}
      <DashboardSection ariaLabel="Insights" label="تحلیل و بینش">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 vania-panel-row">
          <RevenueOverview data={data?.dailySales ?? []} isInitialLoading={isInitialLoading} />
          <CustomersChart data={data?.weeklyCustomers ?? []} isInitialLoading={isInitialLoading} />
        </div>
      </DashboardSection>
    </div>
  );
}

export default DashboardPage;
